package chand.wordvoyage

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import chand.wordvoyage.data.WordEntry
import chand.wordvoyage.platform.createFirestoreRepository
import chand.wordvoyage.ui.WordVoyageTopBar
import chand.wordvoyage.ui.AdBanner
import chand.wordvoyage.viewmodel.WordHistoryState
import chand.wordvoyage.viewmodel.WordHistoryViewModel
import org.jetbrains.compose.resources.painterResource
import wordvoyage.shared.generated.resources.Res
import wordvoyage.shared.generated.resources.ic_chevron_right
import wordvoyage.shared.generated.resources.ic_delete

@Composable
fun WordHistoryScreen(
    userId: String,
    onBack: () -> Unit,
    onDeleteAccount: () -> Unit = {},
    onViewPrivacyPolicy: () -> Unit = {},
    onViewChildrensPrivacy: () -> Unit = {},
    onViewDataControlDeletion: () -> Unit = {},
    onItemClick: (WordEntry) -> Unit = {}
) {
    val firestoreRepository = remember { createFirestoreRepository() }
    val viewModel: WordHistoryViewModel = viewModel {
        WordHistoryViewModel(firestoreRepository, userId)
    }
    val historyState by viewModel.historyState.collectAsState()
    var wordToDelete by remember { mutableStateOf<String?>(null) }

    // Refresh history whenever the screen is shown
    androidx.compose.runtime.LaunchedEffect(Unit) {
        viewModel.loadHistory()
    }

    if (wordToDelete != null) {
        DeleteConfirmationDialog(
            word = wordToDelete!!,
            onConfirm = {
                viewModel.deleteWord(wordToDelete!!)
                wordToDelete = null
            },
            onDismiss = { wordToDelete = null }
        )
    }

    Scaffold(
        topBar = {
            WordVoyageTopBar(
                title = "My Word History",
                onBack = onBack,
                onDeleteAccount = onDeleteAccount,
                onViewPrivacyPolicy = onViewPrivacyPolicy,
                onViewChildrensPrivacy = onViewChildrensPrivacy,
                onViewDataControlDeletion = onViewDataControlDeletion
            )
        },
        bottomBar = { AdBanner() },
        containerColor = MaterialTheme.colorScheme.background
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {

        when (historyState) {
            is WordHistoryState.Loading -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        CircularProgressIndicator()
                        Spacer(modifier = Modifier.height(16.dp))
                        Text("Loading history...", style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }

            is WordHistoryState.Empty -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(
                            text = "📚",
                            style = MaterialTheme.typography.displayMedium
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        Text(
                            text = "No words saved yet",
                            style = MaterialTheme.typography.titleMedium,
                            color = MaterialTheme.colorScheme.onBackground
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = "Generate a word and view its details to save it here",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.6f)
                        )
                    }
                }
            }

            is WordHistoryState.Error -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(
                            text = "⚠ ${(historyState as WordHistoryState.Error).message}",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.error
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        Button(onClick = { viewModel.loadHistory() }) {
                            Text("Retry")
                        }
                    }
                }
            }

            is WordHistoryState.Success -> {
                val entries = (historyState as WordHistoryState.Success).entries
                Text(
                    text = "${entries.size} word${if (entries.size != 1) "s" else ""} learned",
                    style = MaterialTheme.typography.bodyMedium,  // 14sp — MD3 bodyMedium
                    fontWeight = FontWeight.Medium,
                    color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.6f),
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                )
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    items(entries, key = { it.word + it.savedAt }) { entry ->
                        WordHistoryItem(
                            entry = entry,
                            colorIndex = entries.indexOf(entry),
                            onItemClick = onItemClick,
                            onDeleteClick = { wordToDelete = it.word }
                        )
                    }
                    item { Spacer(modifier = Modifier.height(16.dp)) }
                }
            }
        } // end when
        } // end Column
    } // end Scaffold
}

// Rotating palette — vibrant but readable on white card surface
private val cardAccentColors = listOf(
    Color(0xFF6200EA), // Deep Violet
    Color(0xFF0277BD), // Ocean Blue
    Color(0xFF00695C), // Forest Teal
    Color(0xFFAD1457), // Deep Pink
    Color(0xFFE65100), // Burnt Orange
    Color(0xFF2E7D32), // Forest Green
    Color(0xFF4527A0), // Indigo
    Color(0xFF00838F), // Cerulean
    Color(0xFFC62828), // Deep Red
    Color(0xFF00600F), // Dark Green
)

@Composable
private fun WordHistoryItem(
    entry: WordEntry,
    colorIndex: Int,
    onItemClick: (WordEntry) -> Unit,
    onDeleteClick: (WordEntry) -> Unit
) {
    val accent = cardAccentColors[colorIndex % cardAccentColors.size]
    val headerBg = accent
    val bodyBg   = Color.White

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp)
            .clickable { onItemClick(entry) },
        shape = RoundedCornerShape(14.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp),
        colors = CardDefaults.cardColors(containerColor = bodyBg)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {

            // ── Colored header band ──────────────────────────────────────
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(
                        color = headerBg,
                        shape = RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp)
                    )
                    .padding(horizontal = 16.dp, vertical = 8.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    // Word title
                    Text(
                        text = entry.word,
                        style = MaterialTheme.typography.headlineSmall,
                        color = Color.White,
                        letterSpacing = 0.5.sp,
                        modifier = Modifier.weight(1f)
                    )
                    
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        // Date in header
                        Text(
                            text = formatDate(entry.savedAt),
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White.copy(alpha = 0.85f)
                        )
                        Spacer(modifier = Modifier.size(8.dp))
                        IconButton(
                            onClick = { onDeleteClick(entry) },
                            modifier = Modifier.size(24.dp)
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.ic_delete),
                                contentDescription = "Delete word",
                                tint = Color.White,
                                modifier = Modifier.size(18.dp)
                            )
                        }
                    }
                }
            }

            // ── White body ───────────────────────────────────────────────
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(bodyBg)
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = entry.meaning,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    lineHeight = 21.sp,
                    modifier = Modifier.weight(1f)
                )
                Spacer(modifier = Modifier.size(8.dp))
                Icon(
                    painter = painterResource(Res.drawable.ic_chevron_right),
                    contentDescription = "View detail",
                    tint = accent,
                    modifier = Modifier.size(24.dp)
                )
            }
        }
    }
}


@Composable
private fun DeleteConfirmationDialog(
    word: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = "Delete Word?",
                style = MaterialTheme.typography.titleLarge
            )
        },
        text = {
            Text(
                text = "Are you sure you want to remove '$word' from your history?",
                style = MaterialTheme.typography.bodyMedium
            )
        },
        confirmButton = {
            TextButton(
                onClick = onConfirm,
                colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)
            ) {
                Text("Delete", fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}

private fun formatDate(epochMillis: Long): String {
    if (epochMillis == 0L) return ""
    return formatEpochToDate(epochMillis)
}

private fun formatEpochToDate(epochMillis: Long): String {
    val seconds = epochMillis / 1000
    val secondsInDay = 86400L

    // Days since Unix epoch (Jan 1, 1970)
    var daysSinceEpoch = seconds / secondsInDay

    // Calculate year
    var year = 1970
    while (true) {
        val daysInYear = if (isLeapYear(year)) 366L else 365L
        if (daysSinceEpoch < daysInYear) break
        daysSinceEpoch -= daysInYear
        year++
    }

    // Calculate month
    val monthDays = intArrayOf(31, if (isLeapYear(year)) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
    var month = 1
    for (m in monthDays.indices) {
        if (daysSinceEpoch < monthDays[m]) {
            month = m + 1
            break
        }
        daysSinceEpoch -= monthDays[m]
    }
    val day = daysSinceEpoch + 1

    return "${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/$year"
}

private fun isLeapYear(year: Int): Boolean =
    (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)



